// ChannelChecklist.tsx
import * as React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import RadioButtonCheckedRoundedIcon from "@mui/icons-material/RadioButtonCheckedRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { useNavigate } from "react-router-dom";
import colors from "../../Utils/colors";

type Step = {
  id: string;    // identifier used in completionMap
  label: string; // visible title
  href: string;  // route to navigate to on click
};

type Props = {
  channelNumber?: number | string;
  title: string;
  subtitle?: string;
  steps: Step[];
  completionMap: Record<string, boolean>;
  onBlockedAttempt?: (step: Step) => void;
  onClickAction?: (step?: Step) => void;
};

export default function ChannelChecklist({
  channelNumber = 1,
  title,
  subtitle,
  steps,
  completionMap,
  onClickAction,
}: Props) {
  const navigate = useNavigate();

  const isDone = (idx: number) => !!completionMap[steps[idx].id];

  const currentIdx = steps.findIndex((s) => !completionMap[s.id]);

  const handleClick = (step: Step) => {
    if (step.href) {
      navigate(step.href);
    } else {
      onClickAction?.(step);
    }
  };

  return (
    <Card variant="outlined" sx={{ borderRadius: 3, width: "100%" }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
          <Avatar
            variant="rounded"
            sx={{
              width: 48,
              height: 48,
              fontWeight: 700,
              borderRadius: 2,
              bgcolor: colors.white03,
              color: "text.primary",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            {channelNumber}
          </Avatar>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            position: "relative",
            mt: 2,
            "&::before": {
              content: '""',
              position: "absolute",
              left: 24,
              top: 8,
              bottom: 8,
              borderLeft: "1px dotted",
              borderColor: "divider",
            },
          }}
        >
          <List disablePadding>
            {steps.map((step, idx) => {
              const done = isDone(idx);
              const isCurrent =
                !done &&
                idx === (currentIdx === -1 ? steps.length - 1 : currentIdx);

              let Icon = RadioButtonUncheckedRoundedIcon;
              if (done) Icon = CheckCircleRoundedIcon;
              else if (isCurrent) Icon = RadioButtonCheckedRoundedIcon;

              return (
                <ListItem key={step.id} disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => handleClick(step)}
                    sx={{
                      borderRadius: 2,
                      pl: 6,
                      pr: 1.5,
                      alignItems: "center",
                      transition: "background-color 120ms ease",
                      cursor: "pointer",
                      "&:hover": {
                        bgcolor: colors.primary00,
                      },
                      ...(isCurrent
                        ? {
                            // bgcolor: "action.hover",
                            // outline: "1px solid",
                            // outlineColor: "divider",
                          }
                        : {}),
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: 1.5,
                        position: "absolute",
                        left: 12,
                        color: done ? "success.main" : "text.secondary",
                      }}
                    >
                      <Icon fontSize="small" />
                    </ListItemIcon>

                    <ListItemText
                      primary={step.label}
                      primaryTypographyProps={{
                        sx: {
                          fontWeight: done ? 600 : 500,
                          color: "text.primary",
                        },
                      }}
                    />

                    <ChevronRightRoundedIcon sx={{ color: "text.secondary" }} />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Box>
      </CardContent>
    </Card>
  );
}
